import { loadGoogleFont } from './loadGoogleFont'

const SERIF_FONTS = ['Lora', 'Merriweather', 'Noto Serif', 'Roboto Slab', 'Bitter', 'Crimson Pro']
const HEADING_VARIANTS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'strong1', 'strong2']

const DEFAULT_FONT = 'Roboto Variable'

/**
 * Normalizes a font name from the backend. Maps 'Roboto' to the bundled
 * 'Roboto Variable' variable font, then returns null for the default font
 * so it is treated as "no override".
 *
 * @param {string|null|undefined} fontName - The font name from store settings
 * @returns {string|null} Normalized font name, or null if it matches the default
 */
const normalizeFontName = (fontName) => {
    if (!fontName) return null
    const normalized = fontName === 'Roboto' ? DEFAULT_FONT : fontName
    return normalized === DEFAULT_FONT ? null : normalized
}

/**
 * Body-level variants that should inherit the global fontFamily.
 * When a body font override is set, we strip fontFamily from these variants
 * so the final createTheme() re-derives them from the global fontFamily.
 *
 * @see createTypography in @mui/material — it deep-merges passed variant
 * objects over freshly built defaults. If a variant already has fontFamily,
 * the deep merge preserves it, ignoring the new global fontFamily.
 */
const BODY_VARIANTS = [
    'subtitle1',
    'subtitle2',
    'body1',
    'body2',
    'button',
    'caption',
    'overline',
    'small',
    'inputTextSmall',
]

/**
 * Builds a CSS font-family string with the appropriate fallback.
 *
 * @param {string} fontName - The Google Font name
 * @returns {string} CSS font-family value
 */
const buildFontFamily = (fontName) => {
    const fallback = SERIF_FONTS.includes(fontName) ? 'serif' : 'sans-serif'
    return `'${fontName}', ${fallback}`
}

/**
 * Returns a copy of the variant object without the fontFamily property.
 *
 * @param {Object} variant - Typography variant style object
 * @returns {Object} Variant without fontFamily
 */
const stripFontFamily = ({ fontFamily, ...rest }) => rest

/**
 * Applies typography overrides from store settings to the theme.
 * Handles heading font (h1-h6, strong1, strong2) and body font (global fontFamily).
 * Dynamically loads Google Fonts via link element injection.
 *
 * @param {import('@mui/material/styles').Theme} theme - The base MUI theme
 * @param {Object|null} storeSettings - Store customization settings
 * @returns {Object} Modified theme options object
 */
export const applyTypography = (theme, storeSettings) => {
    const headingFont = normalizeFontName(storeSettings?.webStoreHeadingFont)
    const bodyFont = normalizeFontName(storeSettings?.webStoreBodyFont)

    if (!headingFont && !bodyFont) {
        return theme
    }

    loadGoogleFont(headingFont)
    loadGoogleFont(bodyFont)

    const headingOverrides = {}
    if (headingFont) {
        const fontFamily = buildFontFamily(headingFont)
        for (const variant of HEADING_VARIANTS) {
            headingOverrides[variant] = {
                ...theme.typography[variant],
                fontFamily,
            }
        }
    }

    const bodyOverrides = {}
    if (bodyFont) {
        for (const variant of BODY_VARIANTS) {
            if (theme.typography[variant]) {
                bodyOverrides[variant] = stripFontFamily(theme.typography[variant])
            }
        }
    }

    return {
        ...theme,
        typography: {
            ...theme.typography,
            ...(bodyFont && { fontFamily: buildFontFamily(bodyFont) }),
            ...bodyOverrides,
            ...headingOverrides,
        },
    }
}
